import React from 'react';
import { FileBarChart } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';
import { ReportAccessBlock } from './ReportAccessBlock';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface ReportsTabProps {
  enableTimesheetReport: boolean;
  onEnableTimesheetReportChange: (value: boolean) => void;
  viewTimesheetReportAccess: string;
  onViewTimesheetReportAccessChange: (value: string) => void;
  allowTimesheetReportExport: boolean;
  onAllowTimesheetReportExportChange: (value: boolean) => void;
  enableMissingTimesheetReport: boolean;
  onEnableMissingTimesheetReportChange: (value: boolean) => void;
  viewMissingTimesheetReportAccess: string;
  onViewMissingTimesheetReportAccessChange: (value: string) => void;
  allowMissingTimesheetReportExport: boolean;
  onAllowMissingTimesheetReportExportChange: (value: boolean) => void;
  enableUtilisationReport: boolean;
  onEnableUtilisationReportChange: (value: boolean) => void;
  viewUtilisationReportAccess: string;
  onViewUtilisationReportAccessChange: (value: string) => void;
  allowUtilisationReportExport: boolean;
  onAllowUtilisationReportExportChange: (value: boolean) => void;
  enableAuditLog: boolean;
  onEnableAuditLogChange: (value: boolean) => void;
  viewAuditLogAccess: string;
  onViewAuditLogAccessChange: (value: string) => void;
  allowAuditLogExport: boolean;
  onAllowAuditLogExportChange: (value: boolean) => void;
  enablePostingLog: boolean;
  onEnablePostingLogChange: (value: boolean) => void;
  viewPostingLogAccess: string;
  onViewPostingLogAccessChange: (value: string) => void;
  allowPostingLogExport: boolean;
  onAllowPostingLogExportChange: (value: boolean) => void;
  allowPostingLogRetry: boolean;
  onAllowPostingLogRetryChange: (value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

// Hidden blocks keep their stored permission values; they are simply not rendered.
export const ReportsTab: React.FC<ReportsTabProps> = (props) => {
  const { auditTrail, postingLog } = useFeatureFlags();

  return (
  <>
    <BulkActionsDropdown onSelectAll={props.onSelectAll} onDeselectAll={props.onDeselectAll} />
    <Accordion type="multiple" defaultValue={['report-access', 'analytics-insights']} className="space-y-3">

      <SectionAccordionItem value="report-access" icon={<FileBarChart className="h-5 w-5" />} title="Report Access" description="Control access to various reports and analytics">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

          <ReportAccessBlock
            id="enable-timesheet-report"
            label="Timesheet Report"
            description="Access to detailed timesheet reports showing time entries, projects, and tasks"
            enabled={props.enableTimesheetReport}
            onEnabledChange={props.onEnableTimesheetReportChange}
            viewLabel="View Timesheet Report"
            viewDescription="Define which timesheet data users can view in reports"
            viewAccess={props.viewTimesheetReportAccess}
            onViewAccessChange={props.onViewTimesheetReportAccessChange}
            exportDescription="Allow exporting timesheet report data to Excel or CSV"
            allowExport={props.allowTimesheetReportExport}
            onAllowExportChange={props.onAllowTimesheetReportExportChange}
          />

          <ReportAccessBlock
            id="enable-missing-report"
            label="Missing Timesheet Report"
            description="Access to reports showing employees with missing or incomplete timesheets"
            enabled={props.enableMissingTimesheetReport}
            onEnabledChange={props.onEnableMissingTimesheetReportChange}
            viewLabel="View Missing Timesheet Report"
            viewDescription="Define which missing timesheet data users can view in reports"
            viewAccess={props.viewMissingTimesheetReportAccess}
            onViewAccessChange={props.onViewMissingTimesheetReportAccessChange}
            exportDescription="Allow exporting missing timesheet report data to Excel or CSV"
            allowExport={props.allowMissingTimesheetReportExport}
            onAllowExportChange={props.onAllowMissingTimesheetReportExportChange}
          />

          <ReportAccessBlock
            id="enable-utilisation-report"
            label="Utilisation Report"
            description="Access to utilisation reports showing resource allocation and capacity metrics"
            enabled={props.enableUtilisationReport}
            onEnabledChange={props.onEnableUtilisationReportChange}
            viewLabel="View Utilisation Report"
            viewDescription="Define which utilisation data users can view in reports"
            viewAccess={props.viewUtilisationReportAccess}
            onViewAccessChange={props.onViewUtilisationReportAccessChange}
            exportDescription="Allow exporting utilisation report data to Excel or CSV"
            allowExport={props.allowUtilisationReportExport}
            onAllowExportChange={props.onAllowUtilisationReportExportChange}
          />

          {auditTrail && (
          <ReportAccessBlock
            id="enable-audit-log"
            label="Audit Trail"
            description="Access to the Audit Trail report showing full submission, approval, and editing history for timesheets"
            enabled={props.enableAuditLog}
            onEnabledChange={props.onEnableAuditLogChange}
            viewLabel="View Audit Trail"
            viewDescription="Define which timesheet audit records users can view"
            viewAccess={props.viewAuditLogAccess}
            onViewAccessChange={props.onViewAuditLogAccessChange}
            exportDescription="Allow exporting the Audit Trail report to CSV"
            allowExport={props.allowAuditLogExport}
            onAllowExportChange={props.onAllowAuditLogExportChange}
          />
          )}

          {postingLog && (
          <ReportAccessBlock
            id="enable-posting-log"
            label="Posting Log"
            description="Access to the Posting Log report showing ERP posting status for costing transactions"
            enabled={props.enablePostingLog}
            onEnabledChange={props.onEnablePostingLogChange}
            viewLabel="View Posting Log"
            viewDescription="Define which posting records users can view"
            viewAccess={props.viewPostingLogAccess}
            onViewAccessChange={props.onViewPostingLogAccessChange}
            exportDescription="Allow exporting the Posting Log report to CSV"
            allowExport={props.allowPostingLogExport}
            onAllowExportChange={props.onAllowPostingLogExportChange}
            retryDescription="Allow retrying failed postings to the ERP"
            allowRetry={props.allowPostingLogRetry}
            onAllowRetryChange={props.onAllowPostingLogRetryChange}
          />
          )}

          {/* billableHoursReport removed — belongs in the Projects module of the PSA suite. */}

        </div>
      </SectionAccordionItem>
    </Accordion>
  </>
  );
};
